import { useQuery } from '@apollo/client/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buildRoleLabelMap, getDefaultRoleLabel } from './roleLabels';
import { useCompany } from '@/contexts/UserSession';
import type { UserRole } from '@/gql/enums';
import type { CompanyRoleSetting } from '@/pages/CompanyManagementPage/shared/Companies.graphql';
import { FetchCompanyDetails } from '@/pages/CompanyManagementPage/shared/Companies.graphql';

export interface CompanyRoleLabelsHook {
    getRoleLabel: (role: UserRole) => string;
    getCompanyRoles: () => UserRole[];
    roleLabelsMap: Record<UserRole, string>;
    loading: boolean;
    companyId: string | null;
}

/**
 * Resolves role display labels for the active company.
 * By default reads companyId from the session via `useCompany()`.
 *
 * Pass `inputCompanyId` + `token` only for unauthenticated flows (e.g. the
 * pre-login RoleSelectionModal where there is no session yet but a company id
 * has already been resolved from the login step).
 *
 * Falls back to i18n / default labels when company settings cannot be fetched.
 */
const useCompanyRoleLabels = (inputCompanyId?: string, token?: string): CompanyRoleLabelsHook => {
    // This hook is used in many places that can mount on interaction (e.g. disclosure expand).
    // Avoid i18n Suspense here so we don't trigger the route-level <Suspense fallback={<Loader/>}>.
    const { t } = useTranslation('userManagementPage', { useSuspense: false });
    const { companyId: sessionCompanyId } = useCompany();

    const companyId: string | null = useMemo(
        () => inputCompanyId ?? sessionCompanyId,
        [inputCompanyId, sessionCompanyId]
    );

    const { data, loading } = useQuery(FetchCompanyDetails, {
        skip: !companyId,
        variables: { id: companyId as string },
        fetchPolicy: 'cache-first',
        context: token
            ? {
                  headers: {
                      Authorization: `Bearer ${token}`,
                  },
              }
            : undefined,
    });

    const roleLabelsMap = useMemo(() => {
        const companyRoleSettings: CompanyRoleSetting[] = data?.company?.roleSettings ?? [];
        return buildRoleLabelMap(companyRoleSettings, t);
    }, [data, t]);

    const getCompanyRoles = useCallback((): UserRole[] => {
        return data?.company?.roleSettings?.filter(setting => setting.isEnabled).map(setting => setting.role) ?? [];
    }, [data]);

    const getRoleLabel = useCallback(
        (role: UserRole): string => roleLabelsMap[role] ?? getDefaultRoleLabel(role),
        [roleLabelsMap]
    );

    return { getRoleLabel, getCompanyRoles, roleLabelsMap, loading: !!companyId && loading, companyId };
};

export default useCompanyRoleLabels;
